import React from "react";
import { FaCheck, FaWhatsapp, FaPhone, FaArrowRight } from "react-icons/fa";

const plans = [
  {
    name: "Kids Gymnastics — 6 Days",
    price: "₹5,000",
    period: "/month",
    description: "Complete gymnastics program for children aged 4-16",
    features: [
      "6 classes per week",
      "Includes Dance Class",
      "Sports Specific Training",
      "Social Skills Development",
      "Discipline & Focus Training",
      "Gross Motor Skills",
      "Balance and Coordination",
    ],
    highlighted: true,
    badge: "Most Popular",
  },
  {
    name: "Kids Gymnastics — 3 Days",
    price: "₹3,500",
    period: "/month",
    description: "Flexible gymnastics program for children aged 4-16",
    features: [
      "3 classes per week",
      "Sports Specific Training",
      "Social Skills Development",
      "Discipline & Focus Training",
      "Gross Motor Skills",
      "Balance and Coordination",
    ],
    highlighted: false,
    badge: null,
  },
  {
    name: "Fitness Group Class",
    price: "₹3,000",
    period: "/month",
    description: "Group fitness program for adults looking to get fit",
    features: [
      "Group Yoga Classes",
      "CrossFit Training",
      "Cardio Sessions",
      "Strength Training",
      "Plyometrics Training",
    ],
    highlighted: false,
    badge: null,
  },
];

const Pricing = () => {
  return (
    <section className="pt-24 pb-16 bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
            Pricing
          </p>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-slate-400">
            No hidden fees, no surprises. Choose the plan that's right for you
            or your child.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlighted
                  ? "bg-gradient-to-b from-primary-600 to-blue-700 text-white shadow-2xl shadow-primary-600/20 md:scale-105 ring-1 ring-primary-400/50"
                  : "bg-slate-900 border border-slate-800 hover:border-slate-700"
              }`}
            >
              {plan.badge && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold ${
                    plan.highlighted
                      ? "bg-amber-400 text-slate-900"
                      : "bg-white/10 text-white border border-white/20"
                  }`}
                >
                  {plan.badge}
                </div>
              )}

              <h3
                className={`text-xl font-bold mb-1 ${
                  plan.highlighted ? "text-white" : "text-white"
                }`}
              >
                {plan.name}
              </h3>
              <p
                className={`text-sm mb-6 ${
                  plan.highlighted ? "text-blue-200" : "text-slate-500"
                }`}
              >
                {plan.description}
              </p>

              <div className="flex items-baseline gap-1 mb-8">
                <span
                  className={`text-4xl font-black ${
                    plan.highlighted ? "text-white" : "text-blue-400"
                  }`}
                >
                  {plan.price}
                </span>
                <span
                  className={`text-sm ${
                    plan.highlighted ? "text-blue-200" : "text-slate-500"
                  }`}
                >
                  {plan.period}
                </span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm">
                    <FaCheck
                      className={`text-xs mt-1 flex-shrink-0 ${
                        plan.highlighted ? "text-blue-200" : "text-blue-400"
                      }`}
                    />
                    <span
                      className={
                        plan.highlighted ? "text-blue-50" : "text-slate-400"
                      }
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://wa.me/919582691655?text=Hi%2C%20I%27m%20interested%20in%20the%20${encodeURIComponent(plan.name)}%20plan`}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-center py-3.5 rounded-xl font-bold text-sm transition-all ${
                  plan.highlighted
                    ? "bg-white text-primary-600 hover:bg-blue-50 shadow-lg"
                    : "bg-white/5 text-white border border-white/10 hover:bg-primary-600 hover:border-primary-600"
                }`}
              >
                Get Started
              </a>
            </div>
          ))}
        </div>

        {/* Free Trial Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
              Still not sure? Try 2 free classes!
            </h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Experience our training firsthand. No commitment, no pressure.
              Just book a free trial and see the difference.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20trial%20class"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-7 py-4 rounded-full hover:bg-blue-50 transition-all"
              >
                <FaWhatsapp className="text-green-500" />
                Book Free Trial
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="tel:+919582691655"
                className="inline-flex items-center gap-2 text-white font-bold px-6 py-4 rounded-full border border-white/20 hover:border-white/40 transition-colors"
              >
                <FaPhone className="text-sm" />
                +91 95826 91655
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
